import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

// Read base URL from Vite env (baked at build time)
const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://pdf-reform-be.genesis.dev.platform.autonomize.ai';

export interface ApiError {
  status: number;
  message: string;
  data?: unknown;
}

function createAxiosClient(): AxiosInstance {
  const instance = axios.create({
    baseURL: BASE_URL,
    // Increase timeout to 15 minutes for long-running operations (uploads, conversions)
    timeout: 900_000,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    // Important for iOS/iPad to handle CORS properly
    withCredentials: false,
  });

  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    // Attach auth token here if available
    // Example: const token = localStorage.getItem('auth_token');
    // if (token) config.headers = { ...config.headers, Authorization: `Bearer ${token}` } as InternalAxiosRequestConfig['headers'];
    // Ensure headers object exists (Axios v1 types require non-undefined headers)
    if (!config.headers) {
      config.headers = {} as InternalAxiosRequestConfig['headers'];
    }
    return config;
  });

  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      // Check if it's a network error (common on iOS when offline)
      const isNetworkError = !error.response && (
        error.code === 'ERR_NETWORK' ||
        error.code === 'ECONNABORTED' ||
        error.message.includes('Network Error') ||
        error.message.includes('timeout')
      );

      const apiError: ApiError = {
        status: error.response?.status ?? 0,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        message: isNetworkError
          ? 'Network connection unavailable. Using offline data.'
          : ((error.response?.data as any)?.message || error.message),
        data: error.response?.data,
      };

      // Log for debugging on iOS
      if (isNetworkError) {

      }

      return Promise.reject(apiError);
    }
  );

  return instance;
}

export const apiClient = createAxiosClient();


